<!-- eslint-disable vue/no-unused-vars -->
<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('pageDayRateOverview.rateOverview') }} ({{ $route.params.ratePlanType === 'DayRate' ? $t('admin.pageSeasonCalendar.titleRatePlan') : $t('admin.pageSeasonCalendar.titlePackage') }})
    </portal>
    <card v-if="!isEmpty(data)" :title="$t('pageDayRateOverview.rateOverview')" cols="12">
      <hm-card-inner full-size>
        <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('pageDayRateOverview.readOnly') }}</span>
      </hm-card-inner>
      <hm-card-inner>
        <hm-input-date v-model="startDate"
                       :min-date="$moment(minDate, 'YYYYMMDD').add(-1, 'days').format('YYYYMMDD')"
                       :max-date="$moment(maxDate, 'YYYYMMDD').add(-days, 'days').format('YYYYMMDD')"
                       :label="$t('pageDayRateEditor.startDate')"
                       cols="12 2@tablet 2@desktop 1@hd"
                       name="startDate" />
        <hm-input-select v-model="days"
                         :label="$t('pageDayRateEditor.numberOfDays')"
                         name="days"
                         cols="12 2@tablet 2@desktop 1@hd">
          <option :value="7">8</option>
          <option :value="14">15</option>
          <option :value="21">22</option>
          <option :value="28">29</option>
          <option v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" :value="40">40</option>
          <option v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" :value="80">80</option>
          <option v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" :value="120">120</option>
        </hm-input-select>
        <hm-input-toggle v-model="expectedOccupancyOnly"
                         :label="$t('pageDayRateOverview.expectedOccupancyOnly')"
                         :enabled-title="$t('pageDayRateOverview.expectedOccupancyOnlyEnabled')"
                         :disabled-title="$t('pageDayRateOverview.expectedOccupancyOnlyDisabled')"
                         cols="12 6@tablet 4@desktop 2@hd"
                         name="expectedOccupancyOnly" />
      </hm-card-inner>
      <hm-card-inner :title="$t('pageDayRateOverview.card.filter')">
        <hm-multiselect v-model="filterRoomTypes"
                        :label="$t('pageDayRateOverview.filterRoomTypes')"
                        :options="hotelSettings.roomTypes.all()"
                        :limit="3"
                        label-expression="${option.name[$shortLocale]}"
                        cols="start-1 12 6@tablet"
                        name="filterRoomTypes">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('pageDayRateOverview.unselectAll'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               :border="true"
                               :disabled="false"
                               icon="close"
                               @click="filterRoomTypes = []" />
            <input-icon-button v-tooltip="{ title: $t('pageDayRateOverview.selectAll'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               :border="true"
                               :disabled="false"
                               icon="select-all"
                               @click="filterRoomTypes = hotelSettings.roomTypes.all().map(it => it.id)" />
          </template>
        </hm-multiselect>
        <hm-multiselect v-model="filterRatePlans"
                        :label="$t('pageDayRateOverview.filterRatePlans')"
                        :options="hotelSettings.ratePlans.all()"
                        :limit="3"
                        label-expression="${option.name[$shortLocale]}"
                        cols="12 6@tablet"
                        name="filterRatePlans">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button v-tooltip="{ title: $t('pageDayRateOverview.unselectAll'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               :border="true"
                               :disabled="false"
                               icon="close"
                               @click="filterRatePlans = []" />
            <input-icon-button v-tooltip="{ title: $t('pageDayRateOverview.selectAll'), delay: 0, placement: 'right', positionFixed: true, trigger: 'hover', touch: false }"
                               :border="true"
                               :disabled="false"
                               icon="select-all"
                               @click="filterRatePlans = hotelSettings.ratePlans.all().map(it => it.id)" />
          </template>
        </hm-multiselect>
      </hm-card-inner>
      <hm-card-inner :full-size="true">
        <hm-col cols="12">
          <div class="daily-rate-editor__pane">
            <div class="daily-rate-editor__left">
              <div>&nbsp;</div>
              <div>&nbsp;</div>
              <template v-for="roomType in roomTypes">
                <div :key="roomType.id" :title="roomType.name[hotelSettings.details.primaryLanguage]" class="daily-rate-editor__roomType" @click="toggleRoomType(roomType.id)">
                  <icon :class="[{'daily-rate-editor__icon--open': openRoomTypes[roomType.id]}]" class="daily-rate-editor__icon" icon="collapse" /> {{ roomType.name[hotelSettings.details.primaryLanguage] }} <span v-if="!isEmpty(roomType.parentRoomTypeId)" class="text--regular hidden visible@desktop">&nbsp;(derived)</span>
                </div>
                <template v-if="!openRoomTypes[roomType.id]">
                  <div class="daily-rate-editor__label">{{ $t('pageDayRateEditor.available') }}</div>
                  <template v-if="isEmpty(roomType.parentRoomTypeId)">
                    <template v-for="ratePlan in ratePlans">
                      <template v-if="isEmpty(ratePlan.applicableRoomTypes) || ratePlan.applicableRoomTypes.includes(roomType.id)">
                        <!-- Occupancy Rates -->
                        <div class="daily-rate-editor__label daily-rate-editor__label--sub-heading daily-rate-editor__label--sub-heading--regular">
                          <span :title="ratePlan.name[hotelSettings.details.primaryLanguage]" class="daily-rate-editor__label__text">{{ ratePlan.name[hotelSettings.details.primaryLanguage] }}</span>
                          <template v-if="expectedOccupancyOnly">
                            &nbsp;//<icon class="daily-rate-editor__icon" icon="per-person" />&nbsp;x&nbsp;{{ roomType.expectedOccupancy }}
                          </template>
                        </div>
                        <template v-for="numPersons in intArray(roomType.maxOccupancy - roomType.minOccupancy + 1, roomType.minOccupancy)">
                          <template v-if="!expectedOccupancyOnly">
                            <div class="daily-rate-editor__label__wrapper">
                              <div class="daily-rate-editor__label daily-rate-editor__label--2"><icon class="daily-rate-editor__icon" icon="per-person" /> x {{ numPersons }} <template v-if="numPersons === roomType.expectedOccupancy">{{ $t('pageDayRateEditor.defaultOccupancy') }}</template></div>
                              <div class="daily-rate-editor__label--right">{{ hotelSettings.details.currency }}</div>
                            </div>
                          </template>
                        </template>
                      </template>
                    </template>
                  </template>
                </template>
              </template>
            </div>
            <div class="daily-rate-editor__scroll">
              <div :style="`--days: ${Math.min(dates.length)}`" class="daily-rate-editor__wrapper">
                <div v-tooltip="{ title: (seasonDates[date] || {}).name || '-', delay: 0, placement: 'top', positionFixed: true, trigger: 'hover', touch: false }"
                     v-for="date in dates"
                     :key="`season-${date}`"
                     :title="(seasonDates[date] || {}).name || '-'"
                     :style="`background: ${(seasonDates[date] || {}).color || '#fff'}`"
                     class="daily-rate-editor__season"></div>
                <div v-for="date in dates"
                     :class="[
                       {'daily-rate-editor__date--weekend': isWeekend(date)},
                       {'in-selection': date >= selectionStartDate && date <= selectionEndDate },
                     ]"
                     class="daily-rate-editor__date"
                     @mousedown="onMouseDown(date)"
                     @mouseenter="onMouseMove(date)"
                     @mouseup="onMouseUp(date)">
                  <span>{{ date | dayOfWeek }}</span>
                  <span>{{ date | date }}</span>
                  <span>{{ date | month }}</span>
                  <span>{{ date | year }}</span>
                </div>
                <template v-for="roomType in roomTypes">
                  <div v-for="date in dates" :class="[{'daily-rate-editor__placeholder--weekend': isWeekend(date)}]" class="daily-rate-editor__placeholder"></div>
                  <template v-if="!openRoomTypes[roomType.id]">
                    <!-- Availability -->
                    <template v-for="availability in getRoomTypeAvailability(roomType.id)">
                      <div :class="[{'color--red': availability < 0}]" class="daily-rate-editor__value text--bold">
                        {{ availability }}
                      </div>
                    </template>
                    <template v-if="isEmpty(roomType.parentRoomTypeId)">
                      <!-- Occupancy Rates -->
                      <template v-for="ratePlan in ratePlans">
                        <template v-if="isEmpty(ratePlan.applicableRoomTypes) || ratePlan.applicableRoomTypes.includes(roomType.id)">
                          <template v-if="!expectedOccupancyOnly">
                            <div v-for="date in dates" class="daily-rate-editor__value"></div>
                          </template>
                          <template v-for="numPersons in intArray(roomType.maxOccupancy - roomType.minOccupancy + 1, roomType.minOccupancy)">
                            <template v-if="!expectedOccupancyOnly || numPersons === roomType.expectedOccupancy">
                              <template v-for="date in dateStrings">
                                <div class="daily-rate-editor__value">
                                  <span >{{ getRate(roomType, ratePlan, date, numPersons) | toNullableCurrency }}</span>
                                </div>
                              </template>
                            </template>
                          </template>
                        </template>
                      </template>
                    </template>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </hm-col>
      </hm-card-inner>
    </card>

    <spinner-overlay v-if="saving || loading" />
  </main>
</template>

<!-- https://www.hotellinksolutions.com/occupancy-based-pricing-hotel-link/ -->

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, debounce, findIndex, isEmpty, isNil, keyBy, map, sortBy, uniq } from 'lodash';
import HmCol from '../../lib/layout/HmCol';
import HmCardInner from '../../lib/layout/HmCardInner';
import Modal from '../../lib/util/Modal';
import PageMixin from '../../../mixins/PageMixin';
import FilterMixin from '../../../mixins/FilterMixin';
import HmTristate from '../../lib/inputs/HmTriState';
import TimerMixin from '../../../mixins/TimerMixin';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import HmInputNumber from '../../lib/inputs/HmInputNumber.vue';
import UserConfigMixin from '../../../mixins/UserConfigMixin';
import HmInputDailyRateEditor from '../../lib/inputs/HmInputDailyRateEditor.vue';
import fecha from 'fecha';
import HmInputToggle from '../../lib/inputs/HmInputToggle.vue';

export default {
  name: 'PageDayRateOverview',
  components: { HmInputToggle, HmInputDailyRateEditor, HmInputNumber, HmMultiselect, HmCol, HmTristate, Modal, HmCardInner },
  filters: {
    dayOfWeek(data) {
      return new Intl.DateTimeFormat(window.i18n.locale, { weekday: 'short' }).format(
        new Date(`${data.substring(0, 4)}-${data.substring(5, 7)}-${data.substring(8, 10)}`),
      );
    },
    date(data) {
      return parseInt(data.substring(8, 10), 10);
    },
    month(data) {
      const month = data.substring(5, 7);
      return new Intl.DateTimeFormat(window.i18n.locale, { month: 'short' }).format(
        new Date(Date.UTC(2023, parseInt(month, 10) - 1, 1)),
      );
    },
    year(data) {
      return data.substring(0, 4);
    },
  },

  mixins: [HotelmeisterMixin, PageMixin, FilterMixin, TimerMixin, UserConfigMixin],

  data() {
    return {
      ratePlanType: 'DayRate',
      dateSelection: {
        startDate: null,
        endDate: null,
        timeout: null,
      },
      collapseLocked: false,
      leftHeight: 0,
      saving: false,
      loading: false,
      startDate: null,
      days: null,
      data: null,
      roomTypeAvailability: null,
      expectedOccupancyOnly: true,
      openRoomTypes: {},
      openRestrictions: {},
      openAdditionalPersons: {},
      openOccupancyRates: {},
      openDerivedRateOffsets: {},
      seasonDates: {},
      filterRoomTypes: [],
      filterRatePlans: [],
    };
  },

  computed: {
    dateStrings: function () {
      return this.dates.map(it => it.replaceAll('-', ''));
    },
    ratePlans: function () {
      return this.hotelSettings.ratePlans.all()
        .filter(it => it.ratePlanType === this.ratePlanType)
        .filter(it => it.rateEntryMode === 'Daily')
        .filter(it => this.filterRatePlans.includes(it.id));
    },
    selectionStartDate: function () {
      if (isNil(this.dateSelection.startDate)) return 0;
      else return this.dateSelection.startDate; // this.$moment(this.dateSelection.startDate, 'YYYY-MM-DD');
    },
    selectionEndDate: function () {
      if (isNil(this.dateSelection.endDate)) return 0;
      else return this.dateSelection.endDate; // this.$moment(this.dateSelection.endDate, 'YYYY-MM-DD');
    },
    pageShortcuts: function () {
      return {
        'Ctrl+←': this.$t('pageRoomplan.shortcuts.moveBack'),
        'Ctrl+→': this.$t('pageRoomplan.shortcuts.moveForward'),
        'Ctrl+↓': this.$t('pageRoomplan.shortcuts.move1yBack'),
        'Ctrl+↑': this.$t('pageRoomplan.shortcuts.move1yForward'),
        'Ctrl+Pos1': this.$t('pageRoomplan.shortcuts.gotoToday'),
      };
    },
    today() {
      return this.$moment().format('YYYYMMDD');
    },
    maxDate() {
      return this.$moment().add(730, 'days').format('YYYYMMDD');
    },
    minDate() {
      return this.$moment('2023-01-01').format('YYYYMMDD');
    },
    endDate: function () {
      if (isNil(this.startDate)) return null;
      return this.$moment(this.startDate, 'YYYYMMDD').add(this.days, 'days').format('YYYYMMDD');
    },
    dates() {
      const dates = [];
      const currDate = this.$moment(this.startDate, 'YYYYMMDD');
      const lastDate = this.$moment(this.endDate, 'YYYYMMDD');
      const minDate = this.$moment(this.minDate, 'YYYYMMDD');
      const maxDate = this.$moment(this.maxDate, 'YYYYMMDD');

      dates.push(currDate.clone().format('YYYY-MM-DD'));
      while (currDate.add(1, 'days').diff(lastDate) <= 0) {
        // eslint-disable-next-line no-continue
        if (currDate.isBefore(minDate) || currDate.isAfter(maxDate)) continue;
        dates.push(currDate.clone().format('YYYY-MM-DD'));
      }

      return dates;
    },
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    roomTypes: function () {
      return this.hotelSettings.roomTypes.all().filter(it => this.filterRoomTypes.includes(it.id));
    },
    seasonMap: function () {
      return keyBy(this.hotelSettings.seasons.all(), 'id');
    },
  },

  watch: {
    startDate: function (newVal) {
      if (newVal < this.minDate) this.startDate = this.minDate;
      if (newVal > this.maxDate) this.startDate = this.maxDate;
      this.data = null;
      this.loadData();
    },
    endDate: function () {
    },
    days: function () {
      this.data = null;
      this.loadData();
    },
    expectedOccupancyOnly: function () {
      this.data = null;
      this.loadData();
    },
  },

  created: function () {
    this.ratePlanType = this.$route.params.ratePlanType || 'DayRate';
    this.startDate = this.$moment().add(0, 'days').format('YYYYMMDD');

    try {
      this.days = Math.max(14, Math.floor((((Math.max(window.screen.availWidth, window.screen.availHeight) - 300 - 80) / 12 * 10) / (75 + 2)) / 7) * 7);
    } catch (_) {
      this.days = 21;
    }

    this.$serviceBus.$on('shortcut.pageRight', this.pageRight);
    this.$serviceBus.$on('shortcut.pageLeft', this.pageLeft);
    this.$serviceBus.$on('shortcut.pageUp', this.pageRightYear);
    this.$serviceBus.$on('shortcut.pageDown', this.pageLeftYear);
    this.$serviceBus.$on('shortcut.scrollTop', this.openToday);

    this.filterRoomTypes = this.hotelSettings.roomTypes.all().map(it => it.id);
    this.filterRatePlans = this.hotelSettings.ratePlans.all().map(it => it.id);

    this.loadData();
    this.loadSeasonData();
  },

  updated() {
  },

  beforeDestroy() {
    this.$serviceBus.$off('shortcut.pageRight', this.pageRight);
    this.$serviceBus.$off('shortcut.pageLeft', this.pageLeft);
    this.$serviceBus.$off('shortcut.scrollTop', this.openToday);
    this.$serviceBus.$off('shortcut.pageUp', this.pageRightYear);
    this.$serviceBus.$off('shortcut.pageDown', this.pageLeftYear);
  },

  methods: {
    isEmpty,
    isNil,
    openToday: function () {
      this.startDate = this.$moment().add(0, 'days').format('YYYYMMDD');
    },
    pageRight() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(this.days, 'days').format('YYYYMMDD');
    },
    pageLeft() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(-this.days, 'days').format('YYYYMMDD');
    },
    pageRightYear() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(1, 'year').format('YYYYMMDD');
    },
    pageLeftYear() {
      this.startDate = this.$moment(this.startDate, 'YYYYMMDD').add(-1, 'year').format('YYYYMMDD');
    },
    loadSeasonData() {
      const startDate = this.$moment().add(-1, 'days').format('YYYY-MM-DD');
      const endDate = this.$moment().add(this.hotelSettings.details.futureDates, 'days').format('YYYY-MM-DD');

      this.axios.get(this.serviceUrls.seasonDate.list(this, startDate, endDate))
        .then((response) => {
          const lang = this.hotelSettings.details.primaryLanguage;
          this.seasonDates = {
            ...this.seasonDates,
            ...keyBy(response.data.data.map((it) => {
              const season = this.seasonMap[this.ratePlanType === 'DayRate' ? it.season : it.packageSeason];
              return {
                date: fecha.format(fecha.parse(it.date, 'YYYYMMDD'), 'YYYY-MM-DD'),
                id: (this.ratePlanType === 'DayRate' ? it.season : it.packageSeason),
                color: (season || {}).rgbColorCode,
                name: isNil(season) || isNil(season.name) || isEmpty(season.name[lang]) ? null : season.name[lang],
              };
            }), 'date'),
          };
        });
    },
    loadData: debounce(
      function () {
        this.loading = true;
        this.axios.get(this.serviceUrls.dailyRate.overview(this, this.startDate, this.days, this.expectedOccupancyOnly ? 'Default' : 'All'))
          .then((response) => {
            this.data = response.data.data;
          })
          .finally(() => {
            this.loading = false;
          });
        this.axios.get(this.serviceUrls.roomTypePlan(this), {
          params: {
            start: this.startDate,
            limit: this.days,
            cacheBuster: Math.floor(new Date() / 1000),
          },
          headers: {
            Accept: 'application/x-protobuf',
          },
          responseType: 'arraybuffer',
        }).then((response) => {
          this.roomTypeAvailability = response.data.data;
        });
      },
      500,
    ),
    toggleRoomType(roomTypeId) {
      if (isNil(this.openRoomTypes[roomTypeId])) this.$set(this.openRoomTypes, roomTypeId, true);
      else this.openRoomTypes[roomTypeId] = !this.openRoomTypes[roomTypeId];
      if (!this.openRoomTypes[roomTypeId] && this.openRestrictions[roomTypeId]) {
        this.openRestrictions[roomTypeId] = false;
      }
      if (!this.openRoomTypes[roomTypeId] && this.openAdditionalPersons[roomTypeId]) {
        this.openAdditionalPersons[roomTypeId] = false;
      }
      if (!this.openRoomTypes[roomTypeId] && this.openDerivedRateOffsets[roomTypeId]) {
        this.openDerivedRateOffsets[roomTypeId] = false;
      }
      this.$set(this.openOccupancyRates, roomTypeId, this.openRoomTypes[roomTypeId]);
    },
    toggleRoomTypeRestrictions(roomTypeId) {
      if (isNil(this.openRestrictions[roomTypeId])) this.$set(this.openRestrictions, roomTypeId, true);
      else this.openRestrictions[roomTypeId] = !this.openRestrictions[roomTypeId];
    },
    toggleAdditionalPersons(roomTypeId) {
      if (isNil(this.openAdditionalPersons[roomTypeId])) this.$set(this.openAdditionalPersons, roomTypeId, true);
      else this.openAdditionalPersons[roomTypeId] = !this.openAdditionalPersons[roomTypeId];
    },
    toggleOccupancyRates(roomTypeId) {
      if (isNil(this.openOccupancyRates[roomTypeId])) this.$set(this.openOccupancyRates, roomTypeId, true);
      else this.openOccupancyRates[roomTypeId] = !this.openOccupancyRates[roomTypeId];
    },
    toggleDerivedRateOffsets(roomTypeId) {
      if (isNil(this.openDerivedRateOffsets[roomTypeId])) this.$set(this.openDerivedRateOffsets, roomTypeId, true);
      else this.openDerivedRateOffsets[roomTypeId] = !this.openDerivedRateOffsets[roomTypeId];
    },
    intArray: function (value, offset = 0) {
      return [...Array(value).keys()].map(x => x + offset);
    },
    getRate(roomType, ratePlan, date, numPersons) {
      try {
        return this.data.roomTypeMap[roomType.id].ratePlanMap[ratePlan.id].dayRates[date].occupancyRate[numPersons];
      } catch (ignore) {
        return null;
      }
    },
    isWeekend(date) {
      return this.$moment(date).isoWeekday() >= 6;
    },
    getWeekdayLabel(weekdayNumber) { // 0 == Monday
      weekdayNumber += 1;
      if (weekdayNumber > 6) weekdayNumber -= 7;
      return this.$moment.localeData().weekdaysMin()[weekdayNumber]; // 0 = Sunday
    },
    getRoomTypeAvailability(roomTypeId) {
      return this.dates.map((it) => {
        const date = it.replaceAll('-', '');
        if (!isNil(this.roomTypeAvailability) && !isNil(this.roomTypeAvailability.ByRoomType) && !isNil(this.roomTypeAvailability.ByRoomType[date])) {
          return this.roomTypeAvailability.ByRoomType[date].value[roomTypeId];
        } else {
          return '-';
        }
      });
    },
    onFocusRoomTypes() {
      this.collapseLocked = true;
    },
    onBlurRoomTypes() {
      this.setTimeout(() => {
        this.collapseLocked = false;
      }, 500);
    },
    onMouseDown(date) {
      this.dateSelection.startDate = date;
      if (!isNil(this.dateSelection.timeout)) {
        this.clearTimeout(this.dateSelection.timeout);
        this.dateSelection.timeout = null;
      }
      this.dateSelection.timeout = this.setTimeout(() => {
        this.dateSelection.startDate = null;
        this.dateSelection.endDate = null;
        this.dateSelection.timeout = null;
      }, 5000);
    },
    onMouseMove(date) {
      if (!isNil(this.dateSelection.startDate)) {
        this.dateSelection.endDate = date;

        if (!isNil(this.dateSelection.endDate) && this.dateSelection.endDate < this.dateSelection.startDate) {
          this.cancelSelection();
        }
      }
    },
    cancelSelection() {
      if (!isNil(this.dateSelection.timeout)) {
        this.clearTimeout(this.dateSelection.timeout);
        this.dateSelection.startDate = null;
        this.dateSelection.endDate = null;
        this.dateSelection.timeout = null;
      }
    },
    onMouseUp(date) {
      if (!isNil(this.dateSelection.startDate)) {
        if (!isNil(this.dateSelection.timeout)) {
          this.clearTimeout(this.dateSelection.timeout);
          this.dateSelection.timeout = null;
        }
        this.dateSelection.endDate = date;

        if (!isNil(this.dateSelection.endDate) && this.dateSelection.endDate < this.dateSelection.startDate) {
          this.cancelSelection();
        }
      }
      this.dateSelection.startDate = null;
      this.dateSelection.endDate = null;
      this.dateSelection.timeout = null;
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

  .overbooking {
    background-color: rgba($error, 0.3)!important;
  }

  .daily-rate-editor {
    &__pane {
      width: 100%;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 6fr 6fr;
      background-color: $lightmiddlegray;
      grid-gap: 1px;
      @media (min-width: $br-tablet) {
        grid-template-columns: 4fr 8fr;
      }
      @media (min-width: $br-desktop) {
        grid-template-columns: 3fr 10fr;
      }
      @media (min-width: $br-hd) {
        grid-template-columns: 2fr 10fr;
      }
    }
    &__scroll {
      display: flex;
      width: 100%;
      height: auto;
      overflow: auto;
    }
    &__wrapper, &__left {
      display: grid;
      grid-auto-rows: 4rem;
      grid-template-rows: 1rem 9rem;
      grid-gap: 1px;
      width: 100%;
    }
    &__wrapper {
      grid-template-columns: repeat(var(--days), minmax(7.5rem, 1fr));
      overflow-y: hidden;
    }
    &__left {
      grid-template-columns: 1fr;
      min-width: 0;
      > * {
        min-width: 0;
      }
    }
    &__roomType {
      font-weight: bold;
      padding-left: 1rem;
      display: flex;
      height: 100%;
      width: 100%;
      align-items: center;
      background-color: $roomplan-lightgray;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__label {
      min-width: 0;
      padding-right: 1rem;
      padding-left: 3rem;
      display: flex;
      height: 100%;
      width: 100%;
      align-items: center;
      background-color: white;
      &--sub-heading {
        padding-left: 3rem;
        font-weight: bold;
        &--regular {
          font-weight: normal;
        }
      }
      &--2 {
        padding-left: 4rem;
      }
      &--right {
        display: flex;
        padding: 0.5rem;
        align-items: center;
        height: 100%;
        width: 100%;
        color: $middlegray;
        font-weight: bold;
        text-align: center;
        justify-items: center;
        justify-content: center;
        justify-self: center;
        white-space: nowrap;
      }
      &__wrapper {
        display: grid;
        grid-template-columns: auto 6rem;
        background-color: white;
      }
      &__text {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
    &__date {
      text-align: center;
      background-color: $roomplan-lightgray;
      font-weight: bold;
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: repeat(4, 1fr);
      align-items: center;
      grid-gap: 1px;
      position: relative;
      //writing-mode: vertical-rl;
      //text-orientation: mixed;
      &--weekend {
        background-color: darken($lightgray,1%);
      }

      &:not(&--inline-editing) {
        &:after {
          content: ' ';
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: -25000vh;
          width: 100%;
          cursor: default;
        }
        &:hover:after {
          background: rgba($middlemint,.15);
        }
        &.in-selection:after {
          background: rgba($middlemint,.15);
        }
      }
    }
    &__value, &__placeholder {
      overflow: hidden;
      height: 100%;
      width: 100%;
      align-items: center;
      justify-content: right;
      text-align: right;
      padding: 0.4rem;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
    }
    &__input {
      overflow: hidden;
      height: 100%;
      width: 100%;
      align-items: center;
      justify-content: right;
      text-align: right;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
      background: white;

      &.daily-rate-editor__value--inherited {
        grid-template-rows: 5fr 2fr;
      }
    }
    &__inherited-value {
      background-color: $lightgray;
      font-size: 80%;
      margin-left: -0.4rem;
      margin-right: -0.4rem;
      margin-bottom: -0.4rem;
      padding-left: 0.4rem;
      padding-right: 0.4rem;
      padding-top: 0.2rem;
    }
    &__value {
      background-color: white;
      &--inherited {
        grid-template-rows: 5fr 2fr;
      }
      &--auto {
        background-color: lighten($yellowFull, 30%);
      }
      &--warn {
        background-color: lighten($blue, 30%);
      }
      &--error {
        background-color: lighten($error, 30%);
      }
    }
    &__placeholder {
      background-color: $roomplan-lightgray;
      &--weekend {
        background-color: darken($lightgray,1%);
      }
    }
    &__icon {
      min-width: 3rem!important;
      width: 3rem!important;
      max-width: 3rem!important;
      &--open {
        transform: rotate(180deg);
      }
    }
    &__legend {
      box-shadow: 0.1rem 0.1rem 0.2rem 0 rgba($black,0.5);
      padding-bottom: 100%;

      &--auto {
        background-color: lighten($yellowFull, 30%);
      }
      &--warn {
        background-color: lighten($blue, 30%);
        color: white;
      }
      &--error {
        background-color: lighten($error, 30%);
        color: white;
      }

      &__item {
        display: grid;
        grid-template-columns: 30px 1fr;
        grid-template-rows: auto;
        grid-column-gap: 1rem;
        margin-bottom: 1rem;
      }
      &__label {
        align-self: center;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageDayRateOverview.vue